import { Injectable } from '@angular/core';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import moment from 'moment/moment';
import { firstValueFrom, Observable } from 'rxjs';

import { BillParams } from '../data/dietitian/domain/bill/bill-params';
import { Dietitian } from '../data/dietitian/domain/dietitian';
import { DirectoryEntry } from '../data/e-invoicing/domain/directory-entry';
import { EInvoice } from '../data/e-invoicing/domain/e-invoice';
import { EInvoiceEvent } from '../data/e-invoicing/domain/e-invoice-event';
import { EInvoicePage } from '../data/e-invoicing/domain/e-invoice-page';
import {
  FrenchDirectories,
  FrenchDirectoryEntries,
} from '../data/e-invoicing/domain/french-directories';
import { SendingResponse } from '../data/e-invoicing/domain/sending-response';

export interface EInvoicingAccessToken {
  token: string;
  expiredAt: Date;
}

@Injectable()
export class EInvoicingService {
  private readonly exchangeCodeFromCloud: (
    data: unknown,
  ) => Observable<{ token: string; expiresIn: number }>;

  private readonly getAuthorizationUrlFromCloud: (
    data: unknown,
  ) => Observable<string>;

  private readonly refreshTokenFromCloud: (
    data: unknown,
  ) => Observable<{ token: string; expiresIn: number }>;

  private readonly getInvoicesFromCloud: (
    data: unknown,
  ) => Observable<EInvoicePage<EInvoice>>;

  private readonly getInvoiceFromCloud: (data: unknown) => Observable<EInvoice>;

  private readonly getInvoiceEventsFromCloud: (
    data: unknown,
  ) => Observable<EInvoicePage<EInvoiceEvent>>;

  private readonly addInvoiceEventFromCloud: (
    data: unknown,
  ) => Observable<EInvoiceEvent>;

  private readonly frenchDirectoryFromCloud: (
    data: unknown,
  ) => Observable<FrenchDirectories>;

  private readonly frenchDirectoryEntriesFromCloud: (
    data: unknown,
  ) => Observable<FrenchDirectoryEntries>;

  private readonly addInvoiceFromCloud: (
    data: unknown,
  ) => Observable<SendingResponse | undefined>;

  private readonly setCompanyIdentifierFromCloud: (
    data: unknown,
  ) => Observable<string>;

  private readonly refreshDirectoryEntriesFromCloud: (
    data: unknown,
  ) => Observable<void>;

  private readonly getDirectoryEntryFromCloud: (
    data: unknown,
  ) => Observable<DirectoryEntry>;

  private intervalRef: any;

  constructor(private functions: AngularFireFunctions) {
    this.exchangeCodeFromCloud = this.functions.httpsCallable<
      unknown,
      { token: string; expiresIn: number }
    >('e_invoicing-exchangeCode');
    this.getAuthorizationUrlFromCloud = this.functions.httpsCallable<
      unknown,
      string
    >('e_invoicing-getAuthorizationUrl');
    this.refreshTokenFromCloud = this.functions.httpsCallable<
      unknown,
      { token: string; expiresIn: number }
    >('e_invoicing-refreshToken');
    this.getInvoicesFromCloud = this.functions.httpsCallable<
      unknown,
      EInvoicePage<EInvoice>
    >('e_invoicing-getInvoices');
    this.getInvoiceFromCloud = this.functions.httpsCallable<unknown, EInvoice>(
      'e_invoicing-getInvoice',
    );
    this.getInvoiceEventsFromCloud = this.functions.httpsCallable<
      unknown,
      EInvoicePage<EInvoiceEvent>
    >('e_invoicing-getInvoiceEvents');
    this.addInvoiceEventFromCloud = this.functions.httpsCallable<
      unknown,
      EInvoiceEvent
    >('e_invoicing-addInvoiceEvent');
    this.frenchDirectoryFromCloud = this.functions.httpsCallable<
      unknown,
      FrenchDirectories
    >('e_invoicing-frenchDirectory');
    this.frenchDirectoryEntriesFromCloud = this.functions.httpsCallable<
      unknown,
      FrenchDirectoryEntries
    >('e_invoicing-frenchDirectoryEntries');
    this.addInvoiceFromCloud = this.functions.httpsCallable<
      unknown,
      SendingResponse | undefined
    >('e_invoicing-addInvoice');
    this.setCompanyIdentifierFromCloud = this.functions.httpsCallable<
      unknown,
      string
    >('e_invoicing-setCompanyIdentifier');
    this.refreshDirectoryEntriesFromCloud = this.functions.httpsCallable<
      unknown,
      void
    >('e_invoicing-refreshDirectoryEntries');
    this.getDirectoryEntryFromCloud = this.functions.httpsCallable<
      unknown,
      DirectoryEntry
    >('e_invoicing-directoryEntry');
  }

  public hasAuthorization(
    dietitian: Dietitian | undefined,
    billParams: BillParams | undefined,
  ) {
    const connected =
      dietitian &&
      (dietitian.eInvoicingPartnerRefreshStatus == 'connected' ||
        (dietitian.eInvoicingPartnerRefreshToken
          ? dietitian.eInvoicingPartnerRefreshToken.length > 0
          : false));
    if (
      dietitian &&
      dietitian.eInvoicingPartner &&
      dietitian.eInvoicingPartnerNumber &&
      dietitian.eInvoicingPartnerNumberScheme &&
      connected
    ) {
      return true;
    } else {
      if (billParams && billParams.delayedIdentifier) {
        return true;
      }
    }
    return false;
  }

  public getAuthorizationUrl(
    dietitian: Dietitian | undefined,
    sendAndReceiver: string,
  ) {
    return firstValueFrom(
      this.getAuthorizationUrlFromCloud({
        dietitianId: dietitian?.id.toString(),
        sendAndReceiver,
      }),
    );
  }

  async exchangeCode(
    dietitianId: string,
    code: string,
  ): Promise<{ token: string; expiresIn: number }> {
    const response = await firstValueFrom(
      this.exchangeCodeFromCloud({ code, dietitianId }),
    );

    this.saveAccessToken({
      token: response.token,
      expiredAt: moment().add(Number(response.expiresIn), 'seconds').toDate(),
    });
    if (this.intervalRef) {
      clearInterval(this.intervalRef);
      this.intervalRef = undefined;
    }
    this.intervalRef = setInterval(() => {
      this.refreshToken('EInvoicingServce.exchangeCode', dietitianId, false);
    }, (response.expiresIn - 120) * 1000);

    return response;
  }

  getActiveAccessToken(): EInvoicingAccessToken | undefined {
    const token = sessionStorage.getItem('e_invoicing_access_token');
    const expiredAt = sessionStorage.getItem(
      'e_invoicing_access_token_expired_at',
    );

    if (token && expiredAt) {
      const expiredAtDate = new Date(expiredAt);
      if (expiredAtDate > new Date()) {
        return { token, expiredAt: new Date(expiredAt) };
      }
    }
    return undefined;
  }

  async getActiveAccessTokenOrRefresh(caller: string, dietitianId: string) {
    const accessToken = this.getActiveAccessToken();
    if (!accessToken) {
      await this.refreshToken(
        caller + ' -> getActiveAccessTokenOrRefresh',
        dietitianId,
        true,
      );
      return true;
    }
    return true;
  }

  async getInvoices(
    dietitianId: string,
    lastInvoiceId: number | undefined,
  ): Promise<EInvoicePage<EInvoice>> {
    let accessToken = this.getActiveAccessToken();
    if (!accessToken) {
      await this.refreshToken(
        'EInvoicingService.getInvoices',
        dietitianId,
        false,
      );
      accessToken = this.getActiveAccessToken();
    }
    return await firstValueFrom(
      this.getInvoicesFromCloud({
        dietitianId,
        lastInvoiceId,
        accessToken: accessToken!.token,
      }),
    );
  }

  async getInvoice(dietitianId: string, invoiceId: number) {
    let accessToken = this.getActiveAccessToken();
    if (!accessToken) {
      await this.refreshToken(
        'EInvoicingService.getInvoice',
        dietitianId,
        false,
      );
      accessToken = this.getActiveAccessToken();
    }
    return await firstValueFrom(
      this.getInvoiceFromCloud({
        dietitianId,
        invoiceId,
        accessToken: accessToken!.token,
      }),
    );
  }

  async getInvoiceEvents(dietitianId: string, invoiceId: number) {
    let accessToken = this.getActiveAccessToken();
    if (!accessToken) {
      await this.refreshToken(
        'EInvoicingService.getInvoiceEvents',
        dietitianId,
        false,
      );
      accessToken = this.getActiveAccessToken();
    }
    return await firstValueFrom(
      this.getInvoiceEventsFromCloud({
        dietitianId,
        invoiceId,
        accessToken: accessToken!.token,
      }),
    );
  }

  async addInvoiceEvent(
    dietitianId: string,
    invoiceId: number,
    statusCode: string,
    reason?: string | undefined,
  ) {
    let accessToken = this.getActiveAccessToken();
    if (!accessToken) {
      await this.refreshToken(
        'EInvoicingService.addInvoiceEvent',
        dietitianId,
        false,
      );
      accessToken = this.getActiveAccessToken();
    }
    return await firstValueFrom(
      this.addInvoiceEventFromCloud({
        dietitianId,
        invoiceId,
        statusCode,
        reason,
        accessToken: accessToken!.token,
      }),
    );
  }

  async refreshDirectoryEntries(dietitianId: string) {
    let accessToken = this.getActiveAccessToken();
    if (!accessToken) {
      await this.refreshToken(
        'EInvoicingService.refreshDirectoryEntries',
        dietitianId,
        false,
      );
      accessToken = this.getActiveAccessToken();
    }
    return await firstValueFrom(
      this.refreshDirectoryEntriesFromCloud({
        dietitianId,
        accessToken: accessToken!.token,
      }),
    );
  }

  async getDirectoryEntry(dietitianId: string, id: number) {
    let accessToken = this.getActiveAccessToken();
    if (!accessToken) {
      await this.refreshToken(
        'EInvoicingService.getDirectoryEntry',
        dietitianId,
        false,
      );
      accessToken = this.getActiveAccessToken();
    }
    return await firstValueFrom(
      this.getDirectoryEntryFromCloud({
        dietitianId,
        id,
        accessToken: accessToken!.token,
      }),
    );
  }

  async frenchDirectory(dietitianId: string, siren: string) {
    let accessToken = this.getActiveAccessToken();
    if (!accessToken) {
      await this.refreshToken(
        'EInvoicingService.frenchDirectory',
        dietitianId,
        false,
      );
      accessToken = this.getActiveAccessToken();
    }
    return await firstValueFrom(
      this.frenchDirectoryFromCloud({
        dietitianId,
        siren,
        accessToken: accessToken!.token,
      }),
    );
  }

  async frenchDirectoryEntries(dietitianId: string, siren: string) {
    let accessToken = this.getActiveAccessToken();
    if (!accessToken) {
      await this.refreshToken(
        'EInvoicingService.frenchDirectoryEntries',
        dietitianId,
        false,
      );
      accessToken = this.getActiveAccessToken();
    }
    return await firstValueFrom(
      this.frenchDirectoryEntriesFromCloud({
        dietitianId,
        siren,
        accessToken: accessToken!.token,
      }),
    );
  }

  async addInvoice(dietitianId: string, billId: string) {
    let accessToken = this.getActiveAccessToken();
    if (!accessToken) {
      await this.refreshToken(
        'EInvoicingService.addInvoice',
        dietitianId,
        false,
      );
      accessToken = this.getActiveAccessToken();
    }
    return await firstValueFrom(
      this.addInvoiceFromCloud({
        dietitianId,
        billId,
        accessToken: accessToken!.token,
      }),
    );
  }

  async setCompanyIdentifier(dietitianId: string) {
    let accessToken = this.getActiveAccessToken();
    if (!accessToken) {
      await this.refreshToken(
        'EInvoicingService.setCompanyIdentifier',
        dietitianId,
        false,
      );
      accessToken = this.getActiveAccessToken();
    }
    return await firstValueFrom(
      this.setCompanyIdentifierFromCloud({
        dietitianId,
        accessToken: accessToken!.token,
      }),
    );
  }

  formatCurrency(value: string): string {
    if (value === 'EUR') {
      return '€';
    } else {
      return value;
    }
  }

  formatDate(value: string): string {
    const regexp = /(\d{4})-(\d{2})-(\d{2})/;
    if (regexp.test(value)) {
      const parts = value.split('-');
      return parts[2] + '/' + parts[1] + '/' + parts[0];
    }
    return value;
  }

  formatStatusCode(code: string): string {
    if (code.startsWith('api:')) {
      return 'Traitement...';
    } else if (code.startsWith('ppf:')) {
      return 'Transmission...';
    } else if (code.startsWith('fr:')) {
      switch (code) {
        case 'fr:200':
          return 'Déposée';
        case 'fr:201':
          return 'Envoyée';
        case 'fr:202':
          return 'Reçue';
        case 'fr:203':
          return 'Mise à disposition';
        case 'fr:204':
          return 'Reconnue';
        case 'fr:205':
          return 'Acceptée';
        case 'fr:206':
          return 'Partiellement acceptée';
        case 'fr:207':
          return 'Contestée';
        case 'fr:208':
          return 'En attente';
        case 'fr:209':
          return 'Complétée';
        case 'fr:210':
          return 'Refusée';
        case 'fr:211':
          return 'Paiement envoyé';
        case 'fr:212':
          return 'Paiement reçu';
        case 'fr:213':
          return 'Rejetée';
        case 'fr:501':
          return 'Inadmissible';
        default:
          return code;
      }
    } else {
      return code;
    }
  }

  formatPrice(price: string, digit: number) {
    const n = Number(price);
    return n.toFixed(digit);
  }

  formatVat(invoiced_item_vat_rate: string) {
    return invoiced_item_vat_rate + '%';
  }

  formatQuantity(quantity: string, quantityCode: string, digit: number) {
    const q = Number(quantity);
    return this.formatQuantityCode(quantityCode) + q.toFixed(digit);
  }

  private async refreshToken(
    caller: string,
    dietitianId: string,
    withInterval: boolean,
  ) {
    try {
      const response = await firstValueFrom(
        this.refreshTokenFromCloud({ dietitianId, caller }),
      );
      if (response) {
        this.saveAccessToken({
          token: response.token,
          expiredAt: moment()
            .add(Number(response.expiresIn), 'seconds')
            .toDate(),
        });
        if (withInterval) {
          if (this.intervalRef) {
            clearInterval(this.intervalRef);
            this.intervalRef = undefined;
          }
          this.intervalRef = setInterval(() => {
            this.refreshToken(dietitianId, caller, false);
          }, (response.expiresIn - 120) * 1000);
        }
      }
    } catch (e) {
      this.saveAccessToken(undefined);
      throw e;
    }
  }

  private saveAccessToken(accessToken: EInvoicingAccessToken | undefined) {
    if (accessToken) {
      sessionStorage.setItem('e_invoicing_access_token', accessToken.token);
      sessionStorage.setItem(
        'e_invoicing_access_token_expired_at',
        accessToken.expiredAt.toString(),
      );
    }
  }

  private formatQuantityCode(code: string) {
    if (code === 'KGM') {
      return '(Kg) ';
    }
    return '';
  }
}
